import React from 'react';
import { useRegion } from '@/context/RegionContext';

/**
 * Pill toggle showing "GBP £ / USD $" so visitors can switch the pricing
 * display. Doubles as the region selector — flipping currency updates the
 * RegionContext, which propagates to AI tone (for signed-in users), date
 * formats, and any other locale-aware UI.
 *
 * Use the small `<PriceShownNote />` underneath your price grid to tell
 * users the actual charge happens in GBP.
 */
const CurrencyToggle = ({ className = '' }) => {
  const { region, setRegion } = useRegion();
  const isUS = region === 'us';
  return (
    <div
      className={`inline-flex items-center rounded-full border border-white/10 bg-black/20 p-1 text-xs ${className}`}
      role="group"
      aria-label="Display currency"
      data-testid="currency-toggle"
    >
      <button
        type="button"
        onClick={() => setRegion('uk')}
        className={`px-3 py-1 rounded-full transition-colors ${
          !isUS ? 'bg-white text-black font-medium' : 'text-zinc-400 hover:text-white'
        }`}
        data-testid="currency-toggle-gbp"
      >
        GBP £
      </button>
      <button
        type="button"
        onClick={() => setRegion('us')}
        className={`px-3 py-1 rounded-full transition-colors ${
          isUS ? 'bg-white text-black font-medium' : 'text-zinc-400 hover:text-white'
        }`}
        data-testid="currency-toggle-usd"
      >
        USD $
      </button>
    </div>
  );
};

export const PriceShownNote = ({ className = '' }) => {
  const { region } = useRegion();
  if (region === 'uk') return null;
  return (
    <p
      className={`text-xs text-muted-foreground ${className}`}
      data-testid="price-shown-note"
    >
      USD prices are approximate. Your card is charged in GBP and converted by your bank at the live FX rate.
    </p>
  );
};

export default CurrencyToggle;
